import React, { useMemo } from 'react';
import FlagIcon from './FlagIcon';
import './PhoneInput.css';

const COUNTRY_CODE = '+254';

// Kenyan mobile numbers are commonly written with a leading 0 (07XX XXX XXX) even though the
// dialable/stored form drops it once the +254 prefix is present — strip it so a candidate typing
// their number exactly as they'd say it out loud doesn't end up with "+2540712345678".
function stripLeadingZero(digits) {
  return digits.replace(/^0+/, '');
}

/** Same value/onChange contract as a plain <input> (a single "+254712345678"-shaped string) — a
 * drop-in replacement for the old bare `<input type="tel">` fields. The +254 prefix is a fixed,
 * non-editable chip (Kenyan flag + code); the box only ever holds the local digits, so a candidate
 * can't accidentally delete or mistype the country code. Owner ask, 2026-07-25: every phone field
 * in the app should look like this, not a free-text box with a "+254 …" placeholder hint. */
export default function PhoneInput({ id, value, onChange, placeholder = '712 345 678', autoComplete = 'tel', disabled = false }) {
  // Display only the LOCAL part — strip the country code (and a leading 0, just in case) from
  // whatever the parent's `value` currently holds, so editing an already-saved number doesn't show
  // "+254712345678" duplicated inside the box on top of the fixed prefix chip.
  const localDigits = useMemo(() => {
    const raw = String(value || '');
    const withoutCode = raw.startsWith(COUNTRY_CODE) ? raw.slice(COUNTRY_CODE.length) : raw;
    return stripLeadingZero(withoutCode.replace(/\D/g, ''));
  }, [value]);

  function handleChange(e) {
    const digits = stripLeadingZero(e.target.value.replace(/\D/g, '')).slice(0, 9);
    onChange(digits ? `${COUNTRY_CODE}${digits}` : '');
  }

  return (
    <div className="phone-input">
      <span className="phone-input-prefix">
        <FlagIcon country="Kenya" />
        {COUNTRY_CODE}
      </span>
      <input id={id} type="tel" inputMode="tel" autoComplete={autoComplete}
        className="apply-input phone-input-field" placeholder={placeholder}
        value={localDigits} onChange={handleChange} readOnly={disabled} disabled={disabled} />
    </div>
  );
}
